"use client";

import { ArrowRight, ArrowUpRight, Check, Download, Search } from "lucide-react";

import type { GoogleReview } from "@/lib/google-reviews";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isActiveHref, isActiveSection } from "@/lib/active-link";
import { useBookingDrawer } from "@/lib/booking-context";
import { useMediaQuery } from "@/lib/use-media-query";
import type {
  AboutBlogCard,
  AboutMegaContent,
  NavFeatureTile,
  NavLink as NavLinkContent,
  NavSettings,
  PricingMegaContent,
  TestingMegaContent,
  TreatmentsMegaContent,
} from "@/lib/nav";
import { PORTAL_URL, regionFromPathname, regionPath, type Region } from "@/lib/region";
import { useSearch } from "@/lib/search-context";
import { resolveLink } from "@/lib/storyblok-link";
import { switchRegion } from "@/lib/switch-region";

function isHomepage(pathname: string | null): boolean {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  return p === "/" || p === "/uk";
}

function regionSwitcherPaths(pathname: string | null) {
  const path = pathname || "/";
  const base = path.startsWith("/uk/")
    ? path.slice(3)
    : path === "/uk"
      ? "/"
      : path;
  return {
    iePath: base,
    ukPath: base === "/" ? "/uk" : `/uk${base}`,
  };
}


const REGION_RETURN_FOCUS_KEY = "tf:region-return-focus";

function RegionSwitcher({
  region,
  isHero,
}: {
  region: Region;
  isHero: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const pathname = usePathname();
  const { iePath } = regionSwitcherPaths(pathname);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const handleSelect = () => {

    if (keyboardOpen) {
      try {
        sessionStorage.setItem(REGION_RETURN_FOCUS_KEY, "1");
      } catch {}
    }
    setOpen(false);
  };


  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(REGION_RETURN_FOCUS_KEY) !== "1") return;
    sessionStorage.removeItem(REGION_RETURN_FOCUS_KEY);
    const id = requestAnimationFrame(() => triggerRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [region]);

  // ie = 0, uk = 1 — open with focus on the region that's currently selected.
  const activeIndex = region === "uk" ? 1 : 0;

  const itemRingClass = keyboardOpen
    ? "outline-none focus:outline-2 focus:-outline-offset-2 focus:outline-purple-03"
    : "outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-03";

  const handleTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (
      e.key === "Enter" ||
      e.key === " " ||
      e.key === "ArrowDown" ||
      e.key === "ArrowUp"
    ) {
      e.preventDefault();
      setKeyboardOpen(true);
      flushSync(() => setOpen(true));
      itemRefs.current[activeIndex]?.focus();
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  const handleMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    if (items.length === 0) return;
    if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
      return;
    }
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const idx = items.indexOf(e.target as HTMLButtonElement);
    const next =
      e.key === "ArrowDown"
        ? idx < 0
          ? 0
          : (idx + 1) % items.length
        : idx < 0
          ? items.length - 1
          : (idx - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative" onBlur={handleBlur}>
      <Tooltip open={open ? false : tooltipOpen} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            onMouseDown={() => setKeyboardOpen(false)}
            onClick={() => setOpen((v) => !v)}
            onKeyDown={handleTriggerKey}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={`Region: ${region === "uk" ? "United Kingdom" : "Ireland"}`}
            className={`inline-flex cursor-pointer items-center justify-center rounded-full p-1 transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 ${
              isHero
                ? "focus-visible:outline-white/60"
                : "focus-visible:outline-midnight/30"
            }`}
          >
            <span
              aria-hidden
              className={`inline-block size-5 shrink-0 overflow-hidden rounded-full bg-cover bg-center ${
                isHero
                  ? "shadow-[0_0_0_1px_rgba(255,255,255,0.35)]"
                  : "shadow-[0_0_0_1px_rgba(13,10,44,0.15)]"
              }`}
              style={{ backgroundImage: `url(/assets/region/${region}.svg)` }}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent>Change Region</TooltipContent>
      </Tooltip>
      {open ? (
        <div
          role="menu"
          onKeyDown={handleMenuKey}
          className="absolute right-0 top-full mt-3 w-60 rounded-[20px] border border-midnight/6 bg-white p-3 text-midnight shadow-[0_30px_60px_-20px_rgba(13,10,44,0.18),0_8px_16px_-4px_rgba(13,10,44,0.06)]"
        >
          <div className="px-2 pt-1 pb-2 text-[11px] font-bold uppercase tracking-widest text-midnight/50">
            Region
          </div>
          <form action={switchRegion} onSubmit={handleSelect}>
            <input type="hidden" name="to" value="ie" />
            <input type="hidden" name="path" value={iePath} />
            <button
              ref={(el) => {
                itemRefs.current[0] = el;
              }}
              type="submit"
              role="menuitem"
              tabIndex={-1}
              className={`flex w-full items-center gap-2.5 rounded-[10px] px-2 py-2 text-button transition-colors hover:bg-[#f5f3fa] ${itemRingClass} ${
                region === "ie"
                  ? "bg-[#f5f3fa] font-semibold"
                  : "text-midnight/75"
              }`}
            >
              <span
                aria-hidden
                className="inline-block size-4 shrink-0 overflow-hidden rounded-full bg-cover bg-center shadow-[0_0_0_1px_rgba(13,10,44,0.08)]"
                style={{ backgroundImage: "url(/assets/region/ie.svg)" }}
              />
              <span className="flex-1 truncate text-left">Ireland</span>
              {region === "ie" ? (
                <Check
                  className="text-purple-03 size-4 shrink-0"
                  strokeWidth={2.5}
                />
              ) : null}
            </button>
          </form>
          <form action={switchRegion} onSubmit={handleSelect}>
            <input type="hidden" name="to" value="uk" />
            <input type="hidden" name="path" value={iePath} />
            <button
              ref={(el) => {
                itemRefs.current[1] = el;
              }}
              type="submit"
              role="menuitem"
              tabIndex={-1}
              className={`flex w-full items-center gap-2.5 rounded-[10px] px-2 py-2 text-button transition-colors hover:bg-[#f5f3fa] ${itemRingClass} ${
                region === "uk"
                  ? "bg-[#f5f3fa] font-semibold"
                  : "text-midnight/75"
              }`}
            >
              <span
                aria-hidden
                className="inline-block size-4 shrink-0 overflow-hidden rounded-full bg-cover bg-center shadow-[0_0_0_1px_rgba(13,10,44,0.08)]"
                style={{ backgroundImage: "url(/assets/region/uk.svg)" }}
              />
              <span className="flex-1 truncate text-left">United Kingdom</span>
              {region === "uk" ? (
                <Check
                  className="text-purple-03 size-4 shrink-0"
                  strokeWidth={2.5}
                />
              ) : null}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function relativeTime(timestamp: number): string {
  if (!timestamp) return "";
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;
  const days = Math.floor(diff / 86400);
  if (days < 1) return "today";
  if (days === 1) return "a day ago";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return "a week ago";
  if (weeks < 5) return `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return "a month ago";
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(months / 12);
  if (years === 1) return "a year ago";
  return `${years} years ago`;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          viewBox="0 0 20 20"
          className={`size-3.5 ${i < rating ? "text-yellow-400" : "text-midnight/15"}`}
          fill="currentColor"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="size-4" aria-hidden>
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

function formatName(name: string) {
  return name
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

type MegaKey = "pricing" | "treatments" | "testing" | "about";

const NAV_LINKS: {
  label: string;
  href: string;
  mega?: MegaKey;
}[] = [
  { label: "Pricing", href: "/pricing", mega: "pricing" },
  { label: "Success Rates", href: "/success-rates" },
  { label: "Treatments", href: "/treatments", mega: "treatments" },
  { label: "Testing", href: "/testing", mega: "testing" },
  { label: "About", href: "/about", mega: "about" },
];

function MegaLink({ href, title }: { href: string; title: string }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const finalHref = regionPath(href, region);
  // Exact match: mega sub-links point to specific pages, and nested pages
  // (e.g. /testing/.../hycosy) are listed in their own right, so a parent
  // link must not stay highlighted alongside the active child.
  const active = isActiveHref(finalHref, pathname);
  return (
    <Link
      href={finalHref}
      tabIndex={-1}
      aria-current={active ? "page" : undefined}
      className="text-midnight hover:text-purple-03 relative isolate inline-flex w-fit text-[19px] leading-tight font-bold tracking-[-0.01em] transition-colors"
    >
      {active && (
        <span
          aria-hidden
          className="bg-purple-03/15 pointer-events-none absolute -inset-x-2.5 -inset-y-1 -z-10 rounded-lg"
        />
      )}
      {title}
    </Link>
  );
}

function NavMegaLink({
  link,
  region,
}: {
  link: NavLinkContent;
  region: Region;
}) {
  const { href } = resolveLink(link.link, region);
  return <MegaLink href={href} title={link.title || ""} />;
}

function FeatureTile({
  tile,
  region,
}: {
  tile?: NavFeatureTile;
  region: Region;
}) {
  const { setOpen: setBookingOpen } = useBookingDrawer();
  if (!tile) return null;

  const features = (tile.features || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  const cta = tile.cta_link ? resolveLink(tile.cta_link, region) : null;
  const usesBooking = tile.cta_uses_booking ?? true;

  return (
    <aside className="flex h-full min-h-[300px] flex-col rounded-[14px] bg-[#f3eefe] p-7">
      {tile.badge_label && (
        <span className="border-purple-03/20 text-caption text-purple-03 mb-4 inline-flex items-center gap-1.5 self-start rounded-full border bg-white px-2.5 py-1 font-bold tracking-widest uppercase">
          {tile.badge_label}
        </span>
      )}
      {(tile.title_text || tile.title_accent) && (
        <h3 className="text-h6 text-midnight mb-2.5 leading-tight font-bold tracking-[-0.01em]">
          {tile.title_text}
          {tile.title_text && tile.title_accent && " "}
          {tile.title_accent && (
            <span className="text-purple-03">{tile.title_accent}</span>
          )}
        </h3>
      )}
      {tile.description && (
        <p className="mb-5 text-[16px] leading-normal text-[#4a4860]">
          {tile.description}
        </p>
      )}
      {features.length > 0 && (
        <ul className="text-midnight/80 mb-auto flex flex-col gap-2 text-[16px]">
          {features.map((item) => (
            <li key={item} className="flex items-center gap-2.5">
              <span className="bg-purple-03 flex size-4 shrink-0 items-center justify-center rounded-full text-white">
                <Check className="size-2.5" strokeWidth={2.5} />
              </span>
              {item}
            </li>
          ))}
        </ul>
      )}
      {tile.cta_label &&
        (usesBooking ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setBookingOpen(true, "nav-mega-menu")}
            className="bg-midnight text-[16px] hover:bg-purple-03 mt-6 flex h-14 w-full items-center justify-center rounded-2xl px-5 font-bold text-white transition-colors"
          >
            {tile.cta_label}
          </button>
        ) : (
          <a
            href={cta?.href || "#"}
            tabIndex={-1}
            {...(cta?.target ? { target: cta.target } : {})}
            className="bg-midnight text-[16px] hover:bg-purple-03 mt-6 flex h-14 w-full items-center justify-center rounded-2xl px-5 font-bold text-white transition-colors"
          >
            {tile.cta_label}
          </a>
        ))}
    </aside>
  );
}

function MegaShell({
  children,
  gridClass,
  label,
  isOpen,
}: {
  children: React.ReactNode;
  gridClass: string;
  label?: string;
  isOpen?: boolean;
}) {
  return (
    <div
      className={`absolute top-full left-1/2 w-full max-w-[960px] -translate-x-1/2 pt-2 transition-all duration-200 group-hover:visible group-hover:opacity-100 ${
        isOpen ? "visible opacity-100" : "invisible opacity-0"
      }`}
    >
      {/* Hover bridge */}
      <span aria-hidden className="absolute inset-x-0 -top-8 h-10" />
      <div
        className={`relative z-10 origin-top transition-all duration-200 group-hover:translate-y-0 group-hover:scale-100 ${
          isOpen ? "translate-y-0 scale-100" : "-translate-y-1 scale-[0.99]"
        }`}
      >
        {label ? (
          <div className="absolute inset-y-5 left-0 grid -translate-x-[50%] place-items-center rounded-[20px] bg-[#f3eefe] pr-10 pl-3">
            <span className="text-button text-purple-03 rotate-180 pl-3 font-bold tracking-[0.02em] [writing-mode:vertical-rl]">
              {label}
            </span>
          </div>
        ) : null}
        <div
          className={`relative grid ${gridClass} border-midnight/6 text-midnight gap-5 rounded-[20px] border bg-white p-7 shadow-[0_30px_60px_-20px_rgba(13,10,44,0.18),0_8px_16px_-4px_rgba(13,10,44,0.06)]`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function PricingMegaMenu({
  content,
  region,
  isOpen,
}: {
  content?: PricingMegaContent;
  region: Region;
  isOpen?: boolean;
}) {
  if (!content) return null;
  const photo = content.photo?.filename;
  return (
    <MegaShell gridClass="grid-cols-[1fr_0.9fr_1.2fr]" isOpen={isOpen}>
      <div className="flex h-full flex-col justify-between p-6">
        <div>
          {content.heading && (
            <h4 className="mb-5 text-[19px] font-bold text-purple-03">
              {content.heading}
            </h4>
          )}
          <div className="flex flex-col gap-5">
            {(content.links || []).map((link) => (
              <NavMegaLink key={link._uid} link={link} region={region} />
            ))}
          </div>
        </div>
        <div className="border-midnight/8 mt-8 border-t pt-6">
          <p className="text-midnight text-[16px] font-bold">
            Download our pricelist
          </p>
          <p className="text-button mt-1 leading-snug text-[#4a4860]">
            Everything you need to know with no hidden costs.
          </p>
          {content.pricelist && (
            <a
              href={content.pricelist}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={-1}
              className="border-purple-03/30 text-purple-03 text-button hover:bg-purple-03 mt-4 inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 font-bold transition-colors hover:text-white"
            >
              <Download className="size-4" strokeWidth={2.25} />
              Download pricelist
            </a>
          )}
        </div>
      </div>

      <div
        aria-hidden
        className="rounded-[14px] bg-cover bg-position-[center_30%]"
        style={photo ? { backgroundImage: `url(${photo})` } : undefined}
      />

      <FeatureTile tile={content.feature_tile?.[0]} region={region} />
    </MegaShell>
  );
}

function ReviewTile({ review }: { review: GoogleReview }) {
  return (
    <aside className="absolute inset-0 flex flex-col overflow-hidden rounded-[14px] bg-[#f3eefe] p-6">
      <div className="mb-3 flex items-center justify-between">
        <StarRating rating={review.rating} />
        <GoogleIcon />
      </div>
      {/* min-h-0 lets the flex child shrink so overflow-y-auto kicks in. */}
      <div className="relative mb-5 min-h-0 flex-1">
        {/* Focusable so keyboard users can reach the review and scroll it
            with the arrow keys. */}
        <div
          tabIndex={0}
          role="group"
          aria-label="Customer review"
          data-mega-scroll
          className="scrollbar-none h-full overflow-y-auto rounded-lg pr-1 pb-10 outline-none focus-visible:ring-2 focus-visible:ring-purple-03/40 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <p className="text-[16px] leading-[1.55] text-[#4a4860]">
            &ldquo;{review.text}&rdquo;
          </p>
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-linear-to-t from-[#f3eefe] to-transparent"
        />
      </div>
      <div className="flex items-center gap-3">
        {review.authorImage ? (
          <Image
            src={review.authorImage}
            alt=""
            width={36}
            height={36}
            className="size-9 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span className="bg-purple-03/15 text-button text-purple-03 flex size-9 shrink-0 items-center justify-center rounded-full font-bold">
            {review.authorName.charAt(0).toUpperCase()}
          </span>
        )}
        <div className="flex flex-col leading-tight">
          <span className="text-[16px] text-midnight font-bold">
            {formatName(review.authorName)}
          </span>
          <span className="text-[13px] text-midnight/50">{relativeTime(review.time)}</span>
        </div>
      </div>
    </aside>
  );
}

function TreatmentsMegaMenu({
  content,
  region,
  review,
  isOpen,
}: {
  content?: TreatmentsMegaContent;
  region: Region;
  review?: GoogleReview;
  isOpen?: boolean;
}) {
  if (!content) return null;
  const links = content.links || [];
  const wantsImage = content.side_content === "image";
  const photo = content.photo?.filename;
  const showImage = wantsImage && photo;
  const showReview = !showImage && review;

  return (
    <MegaShell gridClass="grid-cols-[1.6fr_1fr]" isOpen={isOpen}>
      <div className="flex flex-col p-6">
        {content.heading && (
          <h4 className="mb-5 text-[19px] font-bold text-purple-03">
            {content.heading}
          </h4>
        )}
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          {links.map((link) => (
            <NavMegaLink key={link._uid} link={link} region={region} />
          ))}
        </div>
        <div className="border-midnight/8 mt-8 border-t pt-5">
          <Link
            href={regionPath("/treatments", region)}
            tabIndex={-1}
            className="text-purple-03 hover:text-purple-04 inline-flex items-center gap-2 text-body font-bold transition-colors"
          >
            View all treatments
            <ArrowRight className="size-5" strokeWidth={2.25} />
          </Link>
        </div>
      </div>

      <div className="relative">
        {showImage ? (
          <div
            aria-hidden
            className="absolute inset-0 rounded-[14px] bg-cover bg-position-[center_30%]"
            style={{ backgroundImage: `url(${photo})` }}
          />
        ) : showReview ? (
          <ReviewTile review={review} />
        ) : null}
      </div>
    </MegaShell>
  );
}

function TestingMegaMenu({
  content,
  region,
  isOpen,
}: {
  content?: TestingMegaContent;
  region: Region;
  isOpen?: boolean;
}) {
  if (!content) return null;
  return (
    <MegaShell gridClass="grid-cols-[2.2fr_1fr]" isOpen={isOpen}>
      <div className="flex flex-col p-6">
        {content.heading && (
          <h4 className="mb-5 text-[19px] font-bold text-purple-03">
            {content.heading}
          </h4>
        )}
        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          {content.women_links && content.women_links.length > 0 && (
            <div>
              {content.women_label && (
                <span className="text-purple-03 mb-3 block text-[11px] font-semibold tracking-[0.12em] uppercase">
                  {content.women_label}
                </span>
              )}
              <div className="flex flex-col gap-5">
                {content.women_links.map((link) => (
                  <NavMegaLink key={link._uid} link={link} region={region} />
                ))}
              </div>
              <Link
                href={regionPath("/testing/women", region)}
                tabIndex={-1}
                className="text-purple-03 hover:text-purple-04 text-button mt-4 inline-flex items-center gap-1.5 font-bold transition-colors"
              >
                View all women’s tests
                <ArrowRight className="size-4" strokeWidth={2.25} />
              </Link>
            </div>
          )}
          {content.men_links && content.men_links.length > 0 && (
            <div>
              {content.men_label && (
                <span className="text-purple-03 mb-3 block text-[11px] font-semibold tracking-[0.12em] uppercase">
                  {content.men_label}
                </span>
              )}
              <div className="flex flex-col gap-5">
                {content.men_links.map((link) => (
                  <NavMegaLink key={link._uid} link={link} region={region} />
                ))}
              </div>
              <Link
                href={regionPath("/testing/men", region)}
                tabIndex={-1}
                className="text-purple-03 hover:text-purple-04 text-button mt-4 inline-flex items-center gap-1.5 font-bold transition-colors"
              >
                View all men’s tests
                <ArrowRight className="size-4" strokeWidth={2.25} />
              </Link>
              {content.additional_links &&
                content.additional_links.length > 0 && (
                  <div className="mt-6">
                    {content.additional_label && (
                      <span className="text-purple-03 mb-3 block text-[11px] font-semibold tracking-[0.12em] uppercase">
                        {content.additional_label}
                      </span>
                    )}
                    <div className="flex flex-col gap-5">
                      {content.additional_links.map((link) => (
                        <NavMegaLink
                          key={link._uid}
                          link={link}
                          region={region}
                        />
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
        <div className="border-midnight/8 mt-8 border-t pt-5">
          <Link
            href={regionPath("/testing", region)}
            tabIndex={-1}
            className="text-purple-03 hover:text-purple-04 inline-flex items-center gap-2 text-body font-bold transition-colors"
          >
            View all tests
            <ArrowRight className="size-5" strokeWidth={2.25} />
          </Link>
        </div>
      </div>

      <div className="relative">
        {(() => {
          const wantsImage = content.side_content === "image";
          const photo = content.photo?.filename;
          const tile = content.feature_tile?.[0];
          if (wantsImage && photo) {
            return (
              <div
                aria-hidden
                className="absolute inset-0 rounded-[14px] bg-cover bg-position-[center_30%]"
                style={{ backgroundImage: `url(${photo})` }}
              />
            );
          }
          if (tile) {
            return (
              <div className="absolute inset-0">
                <FeatureTile tile={tile} region={region} />
              </div>
            );
          }
          return null;
        })()}
      </div>
    </MegaShell>
  );
}

function AboutMegaMenu({
  content,
  region,
  card,
  isOpen,
}: {
  content?: AboutMegaContent;
  region: Region;
  card?: AboutBlogCard | null;
  isOpen?: boolean;
}) {
  if (!content) return null;
  return (
    <MegaShell gridClass="grid-cols-[0.85fr_1.6fr_1.3fr]" isOpen={isOpen}>
      <div className="p-6">
        {content.about_heading && (
          <h4 className="mb-5 text-[19px] font-bold text-purple-03">
            {content.about_heading}
          </h4>
        )}
        <div className="mt-6 flex flex-col gap-5">
          {(content.about_links || []).map((link) => (
            <NavMegaLink key={link._uid} link={link} region={region} />
          ))}
        </div>
      </div>

      <div className="rounded-[14px] bg-[#f3eefe] p-6">
        {content.choosing_heading && (
          <h4 className="mb-5 text-[19px] font-bold text-purple-03">
            {content.choosing_heading}
          </h4>
        )}
        <div className="mt-5 flex flex-col gap-3">
          {(content.choosing_links || []).map((link) => (
            <NavMegaLink key={link._uid} link={link} region={region} />
          ))}
        </div>
      </div>

      {card ? (
        <Link
          href={card.href}
          tabIndex={-1}
          className="group/blog relative flex min-h-[360px] flex-col overflow-hidden rounded-[14px] bg-cover bg-position-[center_25%]"
          style={
            card.image ? { backgroundImage: `url(${card.image})` } : undefined
          }
        >
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-3/5 bg-linear-to-t from-black/80 via-black/45 to-transparent"
          />

          <div className="relative mt-auto flex flex-col p-6">
            {card.badge && (
              <span className="text-caption mb-3 inline-flex items-center gap-1.5 self-start rounded-full border border-white/30 bg-white/15 px-2.5 py-1 font-bold tracking-widest text-white uppercase backdrop-blur-sm">
                {card.badge}
              </span>
            )}
            <h3 className="text-h6 leading-tight font-bold tracking-[-0.01em] text-white">
              {card.title}
            </h3>
            {card.meta && (
              <p className="text-caption mt-2 text-white/70">{card.meta}</p>
            )}
          </div>
        </Link>
      ) : (
        <div aria-hidden />
      )}
    </MegaShell>
  );
}

export default function DesktopNav({
  featuredReview,
  region: initialRegion,
  navSettings,
  aboutBlogCard,
}: {
  featuredReview?: GoogleReview;
  region: Region;
  navSettings?: NavSettings | null;
  aboutBlogCard?: AboutBlogCard | null;
}) {
  const { open: bookingOpen, setOpen: setBookingOpen } = useBookingDrawer();
  const { setOpen: setSearchOpen } = useSearch();
  const pathname = usePathname();
  const region: Region = pathname ? regionFromPathname(pathname) : initialRegion;
  const isHero = isHomepage(pathname);

  const pricingContent = navSettings?.pricing?.[0];
  const treatmentsContent = navSettings?.treatments?.[0];
  const testingContent = navSettings?.testing?.[0];
  const aboutContent = navSettings?.about?.[0];

  // When Pricing has a single entry skip the mega menu and link the
  // nav item straight to that page instead.
  const pricingLinks = pricingContent?.links ?? [];
  const pricingDirectHref =
    pricingLinks.length === 1
      ? resolveLink(pricingLinks[0].link, region).href
      : null;

  const megaSectionLinks = (mega: MegaKey) => {
    if (mega === "pricing") return pricingContent?.links ?? [];
    if (mega === "treatments") return treatmentsContent?.links ?? [];
    if (mega === "testing")
      return [
        ...(testingContent?.women_links ?? []),
        ...(testingContent?.men_links ?? []),
        ...(testingContent?.additional_links ?? []),
      ];
    return [
      ...(aboutContent?.about_links ?? []),
      ...(aboutContent?.choosing_links ?? []),
    ];
  };

  // A top-level item is active for its own URL tree, or when the current page
  // is one of the links inside its mega menu (e.g. /blog lives under About).
  const isNavItemActive = (item: (typeof NAV_LINKS)[number]) => {
    if (isActiveSection(regionPath(item.href, region), pathname)) return true;
    if (!item.mega) return false;
    if (
      megaSectionLinks(item.mega).some((link) =>
        isActiveSection(resolveLink(link.link, region).href, pathname),
      )
    ) {
      return true;
    }
    if (item.mega === "about") {
      const blogHref = aboutContent?.blog_link
        ? resolveLink(aboutContent.blog_link, region).href
        : null;
      if (blogHref && isActiveSection(blogHref, pathname)) return true;
      if (aboutBlogCard?.href && isActiveSection(aboutBlogCard.href, pathname)) {
        return true;
      }
    }
    return false;
  };

  const isCoarse = useMediaQuery("(pointer: coarse)");
  const navRef = useRef<HTMLDivElement>(null);
  const [suppressHover, setSuppressHover] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSuppressHover(true);
    setOpenIndex(null);
    const t = setTimeout(() => setSuppressHover(false), 400);
    return () => clearTimeout(t);
  }, [pathname, bookingOpen]);

  useEffect(() => {
    if (openIndex === null) return;
    const onDown = (e: PointerEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpenIndex(null);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [openIndex]);

  // Refs for the top-level nav items so arrow-key navigation can move
  // focus left/right between them.
  const topRefs = useRef<(HTMLElement | null)[]>([]);
  const [navRove, setNavRove] = useState(0);

  const handleTopKey = (
    e: React.KeyboardEvent<HTMLElement>,
    index: number,
    isMega: boolean,
  ) => {
    const total = NAV_LINKS.length;
    const isLeft = e.key === "ArrowLeft" || e.key === "Left";
    const isRight = e.key === "ArrowRight" || e.key === "Right";
    if (isLeft || isRight) {
      e.preventDefault();
      e.stopPropagation();
      setOpenIndex(null);
      const next = isRight
        ? Math.min(index + 1, total - 1)
        : Math.max(index - 1, 0);
      topRefs.current[next]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      setOpenIndex(null);
      topRefs.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      setOpenIndex(null);
      topRefs.current[total - 1]?.focus();
    } else if (
      isMega &&
      (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")
    ) {
      e.preventDefault();
      const wrapper = e.currentTarget.parentElement;
      flushSync(() => setOpenIndex(index));
      const firstFocusable = wrapper?.querySelector<HTMLElement>(
        '.nav-mega a[href], .nav-mega button:not([aria-haspopup="true"])',
      );
      firstFocusable?.focus();
    } else if (e.key === "Escape") {
      setOpenIndex(null);
    }
  };

  const handleMegaInsideKey = (
    e: React.KeyboardEvent<HTMLDivElement>,
    topIndex: number,
  ) => {
    const target = e.target as HTMLElement;
    if (target.matches('button[aria-haspopup="true"]')) return;
    const total = NAV_LINKS.length;

    if (e.key === "Escape") {
      e.preventDefault();
      setOpenIndex(null);
      topRefs.current[topIndex]?.focus();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      setOpenIndex(null);
      const next = e.shiftKey
        ? (topIndex - 1 + total) % total
        : (topIndex + 1) % total;
      topRefs.current[next]?.focus();
      return;
    }
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(
        '.nav-mega a[href], .nav-mega button:not([aria-haspopup="true"]), .nav-mega [data-mega-scroll]',
      ),
    );
    if (items.length === 0) return;
    if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
      return;
    }
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;

  
    if (target.matches("[data-mega-scroll]")) {
      const down = e.key === "ArrowDown";
      const atTop = target.scrollTop <= 0;
      const atBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 1;
      if (down ? !atBottom : !atTop) {
        e.preventDefault();
        target.scrollBy({ top: down ? 56 : -56 });
        return;
      }
    }

    e.preventDefault();
    const idx = items.indexOf(target);
    const next =
      e.key === "ArrowDown"
        ? idx < 0
          ? 0
          : (idx + 1) % items.length
        : idx < 0
          ? items.length - 1
          : (idx - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const handleNavBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setOpenIndex(null);
    }
  };

  return (
    <div
      ref={navRef}
      className={`group/nav z-50 hidden lg:block ${
        isHero ? "absolute inset-x-0 top-(--region-banner-h,0px)" : "relative"
      }`}
      style={
        isHero
          ? {
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.25) 60%, transparent 100%)",
              paddingBottom: "0px",
            }
          : undefined
      }
    >
      <span
        aria-hidden
        className={`pointer-events-none fixed inset-0 -z-10 bg-black/60 backdrop-blur-[2px] transition-opacity duration-200 ${
          openIndex !== null ? "opacity-100" : "opacity-0"
        } ${isCoarse ? "" : "group-has-[.nav-mega:hover]/nav:opacity-100"}`}
      />
      <nav aria-label="Primary" className={`relative z-10 ${isHero ? "" : "bg-white"}`}>
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-6 px-10 py-8">
          <Link href={regionPath("/", region)} className="block shrink-0" aria-label="Thérapie Fertility">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={
                isHero ? "/assets/logo/logo-w.svg" : "/assets/logo/logo-c.svg"
              }
              alt="Thérapie Fertility Logo"
              width={228}
              height={25}
              className="h-7 w-auto"
            />
          </Link>

          <div className="flex items-center gap-8">
            {NAV_LINKS.map((item, index) => {
              const active = isNavItemActive(item);
              const directHref =
                item.mega === "pricing" && pricingDirectHref
                  ? pricingDirectHref
                  : null;
              return item.mega && !directHref ? (
                <div
                  key={item.label}
                  className={`nav-mega static ${
                    suppressHover ? "**:transition-none!" : "group"
                  }`}
                  onKeyDown={(e) => handleMegaInsideKey(e, index)}
                  onBlur={handleNavBlur}
                >
                  <button
                    ref={(el) => {
                      topRefs.current[index] = el;
                    }}
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={openIndex === index}
                    aria-current={active ? "page" : undefined}
                    tabIndex={index === navRove ? 0 : -1}
                    onFocus={() => setNavRove(index)}
                    onKeyDown={(e) => handleTopKey(e, index, true)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={
                      isCoarse
                        ? () =>
                            setOpenIndex((cur) => (cur === index ? null : index))
                        : undefined
                    }
                    className={`relative isolate inline-flex cursor-default items-center gap-1.5 text-[16px] font-medium whitespace-nowrap transition-colors ${
                      isHero
                        ? openIndex === index
                          ? "text-midnight"
                          : "text-white/92 group-hover:text-midnight group-focus-within:text-midnight"
                        : "text-midnight"
                    }`}
                  >
                    {/* Pill background; pointer-events-none so it doesn't widen the hit area. */}
                    <span
                      aria-hidden
                      className={`pointer-events-none absolute -inset-x-2.5 -inset-y-2 -z-10 rounded-xl transition-opacity duration-200 group-focus-within:opacity-100 group-hover:opacity-100 ${
                        active || openIndex === index ? "opacity-100" : "opacity-0"
                      } ${isHero ? "bg-white" : "bg-[#F3EFFE]"}`}
                    />
                    {item.label}
                  </button>
                  {item.mega === "pricing" ? (
                    <PricingMegaMenu
                      content={pricingContent}
                      region={region}
                      isOpen={openIndex === index}
                    />
                  ) : item.mega === "treatments" ? (
                    <TreatmentsMegaMenu
                      content={treatmentsContent}
                      region={region}
                      review={featuredReview}
                      isOpen={openIndex === index}
                    />
                  ) : item.mega === "testing" ? (
                    <TestingMegaMenu
                      content={testingContent}
                      region={region}
                      isOpen={openIndex === index}
                    />
                  ) : (
                    <AboutMegaMenu
                      content={aboutContent}
                      region={region}
                      card={aboutBlogCard}
                      isOpen={openIndex === index}
                    />
                  )}
                </div>
              ) : (
                <Link
                  key={item.label}
                  ref={(el) => {
                    topRefs.current[index] = el;
                  }}
                  href={directHref ?? regionPath(item.href, region)}
                  aria-current={active ? "page" : undefined}
                  tabIndex={index === navRove ? 0 : -1}
                  onFocus={() => setNavRove(index)}
                  onKeyDown={(e) => handleTopKey(e, index, false)}
                  className={`group relative isolate inline-flex items-center text-[16px] font-medium whitespace-nowrap transition-colors ${
                    isHero
                      ? "text-white/92 hover:text-midnight"
                      : "text-midnight"
                  }`}
                >
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute -inset-x-2.5 -inset-y-2 -z-10 rounded-xl transition-opacity duration-200 group-hover:opacity-100 ${
                      active ? "opacity-100" : "opacity-0"
                    } ${isHero ? "bg-white" : "bg-[#F3EFFE]"}`}
                  />
                  {item.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setSearchOpen(true, "header")}
                  aria-label="Search"
                  className={`max-[1160px]:hidden inline-flex cursor-pointer items-center justify-center rounded-full p-1 transition-colors ${
                    isHero
                      ? "text-white/92 hover:text-white"
                      : "text-midnight hover:text-purple-03"
                  }`}
                >
                  <Search className="size-[18px]" strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipContent>Search</TooltipContent>
            </Tooltip>
            <span
              aria-hidden
              className={`h-4 w-px max-[1160px]:hidden ${isHero ? "bg-white/25" : "bg-midnight/15"}`}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group/portal max-[1160px]:hidden inline-flex items-center gap-1 text-[16px] leading-none font-medium whitespace-nowrap transition-colors ${
                    isHero
                      ? "text-white/92 hover:text-white"
                      : "text-midnight hover:text-purple-03"
                  }`}
                >
                  Portal
                  <ArrowUpRight
                    className="size-[18px] transition-transform group-hover/portal:-translate-y-px group-hover/portal:translate-x-px"
                    strokeWidth={2}
                  />
                </a>
              </TooltipTrigger>
              <TooltipContent>Patient Portal</TooltipContent>
            </Tooltip>
            <span
              aria-hidden
              className={`h-4 w-px max-[1160px]:hidden ${isHero ? "bg-white/25" : "bg-midnight/15"}`}
            />
            <RegionSwitcher region={region} isHero={isHero} />
            <button
              type="button"
              onClick={() => setBookingOpen(true, "desktop-nav")}
              data-track-event="CTA Clicked"
              data-track-label="Free consultation"
              data-track-location="desktop-nav"
              className={`ml-2 inline-flex cursor-pointer items-center justify-center rounded-full px-5 py-2 text-[16px] font-semibold whitespace-nowrap transition-colors ${
                isHero
                  ? "bg-white text-midnight hover:bg-white/90"
                  : "bg-purple-03 text-white hover:bg-purple-04"
              }`}
            >
              Free consultation
            </button>
          </div>
        </div>
      </nav>
    </div>
  );
}
